import { useIntersection } from '@theme/use-intersection'

const ProductRecommendationsConfig = {
  intersectionRootMarginPx: 400
}

class ProductRecommendations extends HTMLElement {
  #unobserve = null
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') {
        continue
      }

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) {
        continue
      }

      this.#loadRecommendations()
      break
    }
  })

  #cachedRecommendations = new Map()
  #activeFetch = null

  connectedCallback() {
    const observer = useIntersection({
      rootMargin: `0px 0px ${ProductRecommendationsConfig.intersectionRootMarginPx}px 0px`
    })
    this.#unobserve = observer.observe(this, (entry) => {
      if (!entry.isIntersecting) {
        return
      }
      observer.unobserve(this)
      this.#loadRecommendations()
    })
    this.#mutationObserver.observe(this, { attributes: true })
  }

  disconnectedCallback() {
    this.#unobserve?.()
    this.#unobserve = null
    this.#mutationObserver.disconnect()
    this.#activeFetch?.abort()
    this.#activeFetch = null
  }

  #loadRecommendations() {
    const recommendationRequest = this.#readRecommendationRequest()
    if (!recommendationRequest) return
    if (recommendationRequest.recommendationsPerformed === 'true') return

    this.#fetchCachedRecommendations(recommendationRequest)
      .then((recommendationResult) =>
        this.#renderRecommendationResult(
          recommendationRequest,
          recommendationResult
        )
      )
      .catch((error) => {
        this.#handleError(error)
      })
  }

  #readRecommendationRequest() {
    const { productId, recommendationsPerformed, sectionId, intent, url } =
      this.dataset
    const elementId = this.id

    if (productId && elementId && url) {
      return {
        elementId,
        intent,
        productId,
        recommendationsPerformed,
        sectionId,
        url
      }
    }

    this.#handleMissingRecommendationRequest(productId, elementId, url)
    return null
  }

  #handleMissingRecommendationRequest(productId, elementId, url) {
    this.#handleError(
      new Error(
        `product-recommendations: received productId="${productId}", id="${elementId}", url="${url}"; expected data-product-id, id, and data-url`
      )
    )
  }

  #renderRecommendationResult(recommendationRequest, recommendationResult) {
    if (!recommendationResult.success) {
      this.#handleFailedRecommendationResult(recommendationResult)
      return
    }

    this.#renderRecommendationsHtml(
      recommendationRequest,
      recommendationResult.html
    )
  }

  #handleFailedRecommendationResult(recommendationResult) {
    if (window.Shopify?.designMode) return

    this.#handleError(
      new Error(
        `product-recommendations: received status="${recommendationResult.status}"; expected 2xx recommendation response`
      )
    )
  }

  #renderRecommendationsHtml(recommendationRequest, responseHtml) {
    const recommendations = this.#extractRecommendations(
      recommendationRequest.elementId,
      responseHtml
    )
    if (recommendations?.innerHTML?.trim().length) {
      this.dataset.recommendationsPerformed = 'true'
      this.innerHTML = recommendations.innerHTML
      return
    }

    this.#handleError(
      new Error(
        `product-recommendations: received empty HTML for id="${recommendationRequest.elementId}"; expected matching product-recommendations content`
      )
    )
  }

  #extractRecommendations(elementId, responseHtml) {
    const responseContainer = document.createElement('div')
    responseContainer.innerHTML = responseHtml
    const selector = `product-recommendations[id="${CSS.escape(elementId)}"]`
    return responseContainer.querySelector(selector)
  }

  async #fetchCachedRecommendations(request) {
    const fetchUrl = this.#buildRecommendationsUrl(request)
    const cachedHtml = this.#cachedRecommendations.get(fetchUrl)
    if (cachedHtml) return { html: cachedHtml, success: true }

    this.#activeFetch?.abort()
    const fetchController = new AbortController()
    this.#activeFetch = fetchController

    try {
      return await this.#fetchRecommendationsHtml(fetchUrl, fetchController)
    } finally {
      if (this.#activeFetch === fetchController) this.#activeFetch = null
    }
  }

  #buildRecommendationsUrl(request) {
    const recommendationsUrl = new URL(request.url, window.location.origin)
    recommendationsUrl.searchParams.set('product_id', request.productId)
    if (request.sectionId)
      recommendationsUrl.searchParams.set('section_id', request.sectionId)
    if (request.intent)
      recommendationsUrl.searchParams.set('intent', request.intent)
    return recommendationsUrl.toString()
  }

  async #fetchRecommendationsHtml(fetchUrl, fetchController) {
    const response = await fetch(fetchUrl, {
      signal: fetchController.signal
    })
    if (!response.ok) return { status: response.status, success: false }

    const responseHtml = await response.text()
    this.#cachedRecommendations.set(fetchUrl, responseHtml)
    return { html: responseHtml, success: true }
  }

  #handleError(error) {
    this.classList.add('hidden')
    this.dataset.error = 'Error loading product recommendations'
    this.dataset.errorDetail = this.#formatErrorDetail(error)
  }

  #formatErrorDetail(error) {
    if (error instanceof Error) return error.message

    const receivedValue = String(error)
    return `product-recommendations: received "${receivedValue}"; expected Error`
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations)
}
